import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  Avatar,
  Tooltip,
  Box,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import WbSunnyIcon from "@material-ui/icons/WbSunny";
import { useLocation } from "react-router-dom";

import MainListItems from "./MainListItems";
import BottomNav from "./BottomNav";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
  },
  toolbar: {
    paddingRight: 24,
    minHeight: 64,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    minHeight: "48px",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.type === "dark" 
      ? alpha(theme.palette.background.paper, 0.95)
      : theme.palette.background.paper,
    backdropFilter: "blur(8px)",
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    boxShadow: "none",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    fontWeight: 600,
    background: theme.palette.type === "dark"
      ? "linear-gradient(45deg, #fff 30%, #f2f2f2 90%)"
      : "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: "60px",
  },
  content: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
  },
  contentWithBottomNav: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
    paddingBottom: 80,
  },
  contentChat: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  iconButton: {
    color: theme.palette.text.primary,
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(1),
  },
  avatar: {
    width: 35,
    height: 35,
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
  },
  menu: {
    "& .MuiPaper-root": {
      marginTop: theme.spacing(1),
      minWidth: 180,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.palette.type === "dark"
        ? "0 8px 16px rgba(0,0,0,0.4)"
        : "0 8px 16px rgba(0,0,0,0.1)",
    },
  },
  menuItem: {
    padding: theme.spacing(1.5, 2),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  appBarLogoWrap: {
    flexGrow: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  appBarLogo: {
    display: "block",
    maxHeight: 36,
    height: 36,
    width: "auto",
    maxWidth: 160,
    objectFit: "contain",
    objectPosition: "left center",
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();

  const isInChatView =
    /^\/tickets\/[^/]+$/.test(location.pathname) ||
    /^\/internal-chats\/[^/]+$/.test(location.pathname);
  const isLockHeightView =
    /^\/tickets(\/[^/]+)?\/?$/.test(location.pathname) ||
    /^\/internal-chats(\/[^/]+)?\/?$/.test(location.pathname);
  const showBottomNav = isMobile && !isInChatView;


  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
      setDrawerOpen(false); // Closed by default on desktop
    }
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const handleDrawerMouseEnter = () => {
    if (drawerVariant === "permanent" && document.body.offsetWidth >= 600) {
      setDrawerOpen(true);
    }
  };

  const handleDrawerMouseLeave = () => {
    if (drawerVariant === "permanent" && document.body.offsetWidth >= 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      {!isMobile && (
        <Drawer
          variant={drawerVariant}
          className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !drawerOpen && classes.drawerPaperClose
            ),
          }}
          open={drawerOpen}
          onMouseEnter={handleDrawerMouseEnter}
          onMouseLeave={handleDrawerMouseLeave}
        >
          <div className={classes.toolbarIcon}>
            {drawerVariant === "temporary" && (
              <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
                <ChevronLeftIcon />
              </IconButton>
            )}
          </div>
          <Divider />
          <List>
            <MainListItems drawerClose={drawerClose} isDrawerOpen={drawerOpen} />
          </List>
          <Divider />
        </Drawer>
      )}
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          !isMobile && drawerOpen && drawerVariant === "permanent" && classes.appBarShift
        )}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {!isMobile && drawerVariant === "temporary" && (
            <IconButton
              edge="start"
              aria-label="open drawer"
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={classes.menuButton}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box className={classes.appBarLogoWrap}>
            <img
              src="/largelogo.png"
              alt="La Estación - CRM"
              className={classes.appBarLogo}
            />
          </Box>

          <div className={classes.themeSwitchContainer}>
            <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"}>
              <IconButton onClick={toggleTheme} className={classes.iconButton}>
                {darkMode ? <WbSunnyIcon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
          </div>

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <Avatar
              onClick={handleMenu}
              className={classes.avatar}
              src={user.profilePic}
            >
              {user.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
              className={classes.menu}
            >
              <MenuItem onClick={handleOpenUserModal} className={classes.menuItem}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout} className={classes.menuItem}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main
        className={clsx(
          classes.content,
          showBottomNav && classes.contentWithBottomNav,
          isLockHeightView && classes.contentChat
        )}
      >
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
      <BottomNav visible={showBottomNav} />
    </div>
  );
};

export default LoggedInLayout;